<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { API_BASE_URL } from '../config'
import MdEditor from '../components/MdEditor.vue'

interface JournalItem {
  id: number
  date: string
  timer: string
  title?: string
  content: string
  weather?: string
  mood?: 'happy' | 'calm' | 'sad' | 'energetic'
  is_public?: boolean
  images: string[]
}

interface DaySummary {
  date: string
  content: string
  mood: 'good' | 'normal' | 'bad'
  tags: string[]
  journals?: JournalItem[]
}

const currentDate = ref(new Date())
const selectedDate = ref<string>('')
const summaries = ref<DaySummary[]>([])
const loading = ref(false)
const isCalendarReady = ref(false) // 日历数据是否加载完成
const titleInput = ref('') // 标题输入框内容
const weatherInput = ref('晴') // 天气
const moodInput = ref<'happy' | 'calm' | 'sad' | 'energetic'>('calm') // 心情
const isPublicInput = ref(true) // 是否公开

// 将日期格式化为 YYYY-MM-DD（使用本地时区）
const formatDate = (date: Date): string => {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

// 获取今天的日期字符串
const getTodayString = () => {
  return formatDate(new Date())
}

// 格式化显示日期（更友好的格式）
const formatDisplayDate = (dateStr: string) => {
  if (!dateStr) return ''
  const date = new Date(dateStr)
  const year = date.getFullYear()
  const month = date.getMonth() + 1
  const day = date.getDate()
  return `${year}年${month}月${day}日`
}

// API响应接口
interface ApiResponse {
  code: number
  message: string
  data: JournalItem[]
}

// 存储当月所有有日志的日期
const journalDates = ref<string[]>([])

// 从API获取当月日志列表
const fetchJournalList = async (year: number, month: number) => {
  // 标记日历未就绪，等待数据加载
  isCalendarReady.value = false
  
  try {
    const monthStr = `${year}-${String(month + 1).padStart(2, '0')}`
    console.log('Fetching journal list for:', monthStr)
    const response = await fetch(`${API_BASE_URL}/api/journal?date=${monthStr}`)
    if (response.ok) {
      const result = await response.json()
      console.log('API response:', result)
      if (result.code === 1 && result.data && result.data.length > 0) {
        // 过滤出当前月份的数据（兼容后端返回其他月份数据的情况）
        const filteredData = result.data.filter((item: any) => 
          item.date && item.date.startsWith(monthStr)
        )
        console.log('Filtered data for', monthStr, ':', filteredData)
        
        // 提取所有有日志的日期
        journalDates.value = filteredData.map((item: any) => item.date)
        console.log('Journal dates updated:', journalDates.value)
        // 将数据转换为DaySummary格式存储
        summaries.value = filteredData.map((item: any) => ({
          date: item.date,
          content: item.content,
          mood: 'normal' as const,
          tags: [],
          journals: [{
            id: item.id,
            date: item.date,
            timer: '00:00',
            title: item.title || '',
            content: item.content,
            weather: item.weather || '晴',
            mood: item.mood || 'calm',
            is_public: item.is_public !== undefined ? item.is_public : true,
            images: []
          }]
        }))
      } else {
        journalDates.value = []
        summaries.value = []
        console.log('No journal data found')
      }
    }
    // 数据加载完成后，如果当前没有选中日期，自动选中今天
    if (!selectedDate.value) {
      const today = getTodayString()
      selectedDate.value = today
      // 标记编辑器就绪（初始加载时）
      setTimeout(() => {
        isEditorReady.value = true
      }, 100)
    }
    // 数据加载完成，标记日历就绪
    isCalendarReady.value = true
  } catch (error) {
    console.error('获取日志列表失败:', error)
    journalDates.value = []
    summaries.value = []
    // 即使失败也标记就绪，显示空日历
    isCalendarReady.value = true
  }
}

// 切换选中的日期（直接使用已加载的数据）
const selectDate = (date: string) => {
  selectedDate.value = date
}

// 选中的日期变化时，加载已有内容到编辑器
watch(selectedDate, (newDate) => {
  if (newDate) {
    // 先标记编辑器未就绪，避免触发自动保存
    isEditorReady.value = false
    
    const summary = summaries.value.find(s => s.date === newDate)
    if (summary?.journals && summary.journals.length > 0) {
      // 加载已有内容到编辑器
      journalContent.value = summary.journals[0].content
      // 如果有保存的标题就使用，否则使用格式化日期
      titleInput.value = summary.journals[0].title || formatDisplayDate(newDate)
      // 加载天气、心情、公开状态
      weatherInput.value = summary.journals[0].weather || '晴'
      moodInput.value = summary.journals[0].mood || 'calm'
      isPublicInput.value = summary.journals[0].is_public !== undefined ? summary.journals[0].is_public : true
    } else {
      // 清空编辑器
      journalContent.value = ''
      // 设置默认标题为格式化日期
      titleInput.value = formatDisplayDate(newDate)
      // 重置为默认值
      weatherInput.value = '晴'
      moodInput.value = 'calm'
      isPublicInput.value = true
    }
    
    // 延迟标记编辑器就绪，让编辑器完成初始化
    setTimeout(() => {
      isEditorReady.value = true
    }, 100)
  }
})

const year = computed(() => currentDate.value.getFullYear())
const month = computed(() => currentDate.value.getMonth())

// 监听月份变化，自动获取当月数据
watch([year, month], ([newYear, newMonth]) => {
  fetchJournalList(newYear, newMonth)
}, { immediate: true })

const monthNames = ['一月', '二月', '三月', '四月', '五月', '六月', '七月', '八月', '九月', '十月', '十一月', '十二月']
const weekDays = ['日', '一', '二', '三', '四', '五', '六']

const calendarDays = computed(() => {
  const firstDay = new Date(year.value, month.value, 1)
  const lastDay = new Date(year.value, month.value + 1, 0)
  const startDate = new Date(firstDay)
  startDate.setDate(startDate.getDate() - firstDay.getDay())
  
  const days = []
  const today = formatDate(new Date())
  
  for (let i = 0; i < 42; i++) {
    const date = new Date(startDate)
    date.setDate(startDate.getDate() + i)
    const dateStr = formatDate(date)
    const summary = summaries.value.find(s => s.date === dateStr)
    // 检查该日期是否有日志（用于显示绿色点）
    const hasJournal = journalDates.value.includes(dateStr)
    
    days.push({
      date: dateStr,
      day: date.getDate(),
      isCurrentMonth: date.getMonth() === month.value,
      isToday: dateStr === today,
      summary,
      hasJournal,
    })
  }
  
  return days
})

const selectedSummary = computed(() => {
  if (!selectedDate.value) return null
  return summaries.value.find(s => s.date === selectedDate.value)
})

const prevMonth = () => {
  currentDate.value = new Date(year.value, month.value - 1, 1)
  // watch([year, month]) 会自动触发 fetchJournalList
}

const nextMonth = () => {
  currentDate.value = new Date(year.value, month.value + 1, 1)
  // watch([year, month]) 会自动触发 fetchJournalList
}

const getMoodIcon = (mood: string) => {
  const icons: Record<string, string> = {
    good: '😊',
    normal: '😐',
    bad: '😔',
  }
  return icons[mood] || ''
}

const previewImage = ref('')
const showPreview = ref(false)
const journalContent = ref('')
const mdEditorRef = ref<InstanceType<typeof MdEditor> | null>(null)
const isSaving = ref(false)
const isEditorReady = ref(false) // 编辑器是否初始化完成

const openImage = (url: string) => {
  previewImage.value = url
  showPreview.value = true
}

const closePreview = () => {
  showPreview.value = false
  previewImage.value = ''
}

// 直接保存 HTML，不再转换为 Markdown

// 保存日志到服务器
const saveJournal = async () => {
  if (!journalContent.value.trim() || !selectedDate.value || isSaving.value) return
  
  isSaving.value = true
  
  try {
    // 直接保存 HTML 内容
    const htmlContent = journalContent.value
    
    const response = await fetch(`${API_BASE_URL}/api/journal/save`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        date: selectedDate.value,
        title: titleInput.value,
        content: htmlContent,
        weather: weatherInput.value,
        mood: moodInput.value,
        is_public: isPublicInput.value ? 1 : 0,
      }),
    })
    
    if (response.ok) {
      // 更新本地数据，不刷新整个列表
      const result = await response.json()
      if (result.code === 1) {
        // 更新或添加当前日期的本地数据
        const existingIndex = summaries.value.findIndex(s => s.date === selectedDate.value)
        const journalData = {
          date: selectedDate.value,
          content: htmlContent,
          mood: 'normal' as const,
          tags: [],
          journals: [{
            id: result.data?.id || Date.now(),
            date: selectedDate.value,
            timer: new Date().toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' }),
            title: titleInput.value,
            content: htmlContent,
            weather: weatherInput.value,
            mood: moodInput.value,
            is_public: isPublicInput.value,
            images: []
          }]
        }
        if (existingIndex >= 0) {
          summaries.value[existingIndex] = journalData
        } else {
          summaries.value.push(journalData)
        }
        // 更新日历上的绿色标记
        if (!journalDates.value.includes(selectedDate.value)) {
          journalDates.value.push(selectedDate.value)
        }
      }
    }
  } catch (error) {
    console.error('保存日志失败:', error)
  } finally {
    isSaving.value = false
  }
}

// 监听内容变化，自动保存（防抖）
let saveTimeout: number | null = null
watch(journalContent, (newValue) => {
  // 编辑器未就绪或内容为空时不触发保存
  if (!isEditorReady.value || !newValue || !newValue.trim()) return
  
  if (saveTimeout) {
    clearTimeout(saveTimeout)
  }
  saveTimeout = window.setTimeout(() => {
    saveJournal()
  }, 2000) // 2秒后自动保存
})
</script>

<template>
  <div class="summary-page">
    <!-- 左侧日历 -->
    <aside class="calendar-section">
      <div class="calendar-header">
        <button class="nav-btn" @click="prevMonth">‹</button>
        <h3>{{ year }}年 {{ monthNames[month] }}</h3>
        <button class="nav-btn" @click="nextMonth">›</button>
      </div>
      <div class="weekdays">
        <span v-for="day in weekDays" :key="day" class="weekday">{{ day }}</span>
      </div>
      <!-- 日历加载中显示占位 -->
      <div v-if="!isCalendarReady" class="calendar-loading">
        <div class="calendar-grid-placeholder">
          <div v-for="n in 42" :key="n" class="day-cell-placeholder"></div>
        </div>
      </div>
      <div v-else class="calendar-grid">
        <button
          v-for="day in calendarDays"
          :key="day.date"
          class="day-cell"
          :class="{
            'other-month': !day.isCurrentMonth,
            'today': day.isToday,
            'selected': selectedDate === day.date,
            'has-journal': day.hasJournal,
          }"
          @click="selectDate(day.date)"
        >
          <span class="day-number">{{ day.day }}</span>
        </button>
      </div>
    </aside>

    <!-- 右侧详情 -->
    <main class="detail-section">
      <div v-if="loading" class="empty-detail">
        <p>加载中...</p>
      </div>
      <div v-else-if="selectedDate" class="summary-detail">
        <div class="detail-header">
          <input 
            type="text" 
            v-model="titleInput" 
            class="title-input"
            placeholder="输入标题..."
          />
          
          <!-- 天气、心情、公开状态 -->
          <div class="meta-fields">
            <div class="meta-item">
              <label>天气</label>
              <select v-model="weatherInput" class="meta-select">
                <option value="晴">☀️ 晴</option>
                <option value="阴">☁️ 阴</option>
                <option value="雨">🌧️ 雨</option>
                <option value="雪">❄️ 雪</option>
                <option value="多云">⛅ 多云</option>
                <option value="大风">💨 大风</option>
              </select>
            </div>
            
            <div class="meta-item">
              <label>心情</label>
              <div class="mood-buttons">
                <button 
                  class="mood-btn"
                  :class="{ active: moodInput === 'happy' }"
                  @click="moodInput = 'happy'"
                  title="开心"
                >😊</button>
                <button 
                  class="mood-btn"
                  :class="{ active: moodInput === 'calm' }"
                  @click="moodInput = 'calm'"
                  title="平静"
                >😌</button>
                <button 
                  class="mood-btn"
                  :class="{ active: moodInput === 'sad' }"
                  @click="moodInput = 'sad'"
                  title="忧郁"
                >😔</button>
                <button 
                  class="mood-btn"
                  :class="{ active: moodInput === 'energetic' }"
                  @click="moodInput = 'energetic'"
                  title="充实"
                >💪</button>
              </div>
            </div>
            
            <div class="meta-item">
              <label>公开</label>
              <button 
                class="toggle-btn"
                :class="{ active: isPublicInput }"
                @click="isPublicInput = !isPublicInput"
              >
                {{ isPublicInput ? '是' : '否' }}
              </button>
            </div>
          </div>
          
          <button class="save-btn" @click="saveJournal" :disabled="!journalContent.trim() || isSaving">
            {{ isSaving ? '保存中...' : '保存' }}
          </button>
        </div>
        
        <!-- 编辑器表单 -->
        <div class="write-form">
          <MdEditor ref="mdEditorRef" v-model="journalContent" placeholder="写下今天的总结..." :showSaveButton="false" :noMaxWidth="true" uploadPath="/api/upload/journal" />
          
        </div>
        
        <div class="journal-content-area">
          <div v-if="selectedSummary?.journals && selectedSummary.journals.length > 0" class="journal-list"></div>

        </div>
      </div>
      <div v-else-if="selectedDate" class="empty-detail">
        <p>{{ selectedDate }} 暂无总结</p>
        <button class="add-btn">+ 添加今日总结</button>
      </div>
      <div v-else class="empty-detail">
        <p>请选择日期查看总结</p>
      </div>
    </main>

    <!-- 图片预览弹窗 -->
    <div v-if="showPreview" class="image-preview-overlay" @click.self="closePreview">
      <div class="image-preview-content">
        <button class="close-preview-btn" @click="closePreview">×</button>
        <img :src="previewImage" alt="预览图片" />
      </div>
    </div>
  </div>
</template>

<style scoped>
.summary-page {
  display: flex;
  height: 100%;
  background: #f5f5f5;
}

/* 左侧日历 */
.calendar-section {
  width: 400px;
  background: #fff;
  border-right: 1px solid #e0e0e0;
  padding: 1.5rem;
  flex-shrink: 0;
}

.calendar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.calendar-header h3 {
  margin: 0;
  font-size: 1.1rem;
  color: #333;
}

.nav-btn {
  width: 32px;
  height: 32px;
  border: none;
  background: #f0f0f0;
  border-radius: 50%;
  cursor: pointer;
  font-size: 1.2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
}

.nav-btn:hover {
  background: #e0e0e0;
}

.weekdays {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.weekday {
  text-align: center;
  font-size: 0.85rem;
  color: #999;
  padding: 0.5rem;
}

.calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 0.5rem;
}

/* 日历加载占位样式 */
.calendar-grid-placeholder {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 0.5rem;
}

.day-cell-placeholder {
  aspect-ratio: 1;
  border-radius: 8px;
  background: #f5f5f5;
  animation: pulse 1.5s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 0.6;
  }
  50% {
    opacity: 1;
  }
}

.day-cell {
  aspect-ratio: 1;
  border: none;
  background: transparent;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  transition: all 0.2s;
  position: relative;
}

.day-cell:hover {
  background: #f0f0f0;
}

.day-cell.other-month {
  color: #ccc;
}

.day-cell.today {
  background: #e8f5e9;
  color: #42b883;
  font-weight: 600;
}

.day-cell.selected {
  background: #42b883;
  color: #fff;
}

.day-cell.has-journal::after {
  content: '';
  position: absolute;
  bottom: 4px;
  width: 6px;
  height: 6px;
  background: #42b883;
  border-radius: 50%;
}

.day-cell.selected.has-journal::after {
  background: #fff;
}

.day-number {
  font-size: 0.9rem;
}

.mood-icon {
  font-size: 1rem;
}

/* 右侧详情 */
.detail-section {
  flex: 1;
  padding: 1.5rem;
  overflow-y: auto;
}

.summary-detail {
  background: #fff;
  border-radius: 8px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  display: flex;
  flex-direction: column;
  height: 100%;
}

.detail-header {
  display: flex;
  justify-content: center;
  align-items: center;
  padding-bottom: 1rem;
  position: relative;
}

.detail-header h2 {
  margin: 0;
  font-size: 1.1rem;
  color: #333;
}

.title-input {
  position: absolute;
  left: 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: #333;
  border: none;
  outline: none;
  background: transparent;
  padding: 0;
  margin: 0;
  min-width: 200px;
}

.title-input:focus {
  border: none;
  outline: none;
}

.title-input::placeholder {
  color: #999;
  font-weight: 400;
}

/* 元数据字段 */
.meta-fields {
  display: flex;
  gap: 1.5rem;
  align-items: center;
  justify-content: center;
  margin: 0 2rem;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.meta-item label {
  font-size: 0.85rem;
  color: #666;
  font-weight: 500;
}

.meta-select {
  padding: 0.4rem 0.8rem;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  font-size: 0.85rem;
  color: #333;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.2s;
}

.meta-select:hover {
  border-color: #42b883;
}

.meta-select:focus {
  outline: none;
  border-color: #42b883;
}

.mood-buttons {
  display: flex;
  gap: 0.4rem;
}

.mood-btn {
  width: 2rem;
  height: 2rem;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.mood-btn:hover {
  border-color: #42b883;
  background: #f0faf6;
}

.mood-btn.active {
  border-color: #42b883;
  background: #42b883;
}

.toggle-btn {
  padding: 0.4rem 1rem;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
  font-size: 0.85rem;
  color: #666;
  transition: all 0.2s;
}

.toggle-btn:hover {
  border-color: #42b883;
}

.toggle-btn.active {
  border-color: #42b883;
  background: #42b883;
  color: #fff;
}

.save-btn {
  position: absolute;
  right: 0;
  padding: 0.4rem 1.2rem;
  background: #42b883;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.85rem;
  transition: background 0.2s;
}

.save-btn:hover:not(:disabled) {
  background: #369970;
}

.save-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.write-form {

}

.write-form textarea {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  font-size: 0.9rem;
  resize: vertical;
  font-family: inherit;
  box-sizing: border-box;
}

.write-form textarea:focus {
  outline: none;
  border-color: #42b883;
}

.form-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 0.75rem;
}

.upload-image-btn {
  padding: 0.5rem 1rem;
  background: #fff;
  color: #666;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.85rem;
  transition: all 0.2s;
}

.upload-image-btn:hover {
  border-color: #42b883;
  color: #42b883;
}

.submit-btn {
  padding: 0.5rem 1.5rem;
  background: #42b883;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: background 0.2s;
}

.submit-btn:hover {
  background: #369970;
}

.mood-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 16px;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.mood-badge.good {
  background: #e8f5e9;
  color: #42b883;
}

.mood-badge.normal {
  background: #fff3e0;
  color: #ff9800;
}

.mood-badge.bad {
  background: #ffebee;
  color: #f44336;
}

.summary-content {
  font-size: 1rem;
  line-height: 1.8;
  color: #333;
  margin: 0 0 1rem 0;
}

.journal-content-area {
  min-height: 375px;
}

.journal-list {
  margin: 1rem 0;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.empty-journal {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 375px;
  color: #999;
}

.empty-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
  opacity: 0.5;
}

.empty-journal p {
  font-size: 0.95rem;
}

.journal-time {
  font-size: 0.8rem;
  color: #999;
  margin-bottom: 0.5rem;
}

.journal-content {
  font-size: 0.95rem;
  color: #333;
  line-height: 1.6;
  margin: 0 0 0.75rem 0;
}

.journal-images {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.journal-images img {
  width: 120px;
  height: 120px;
  object-fit: cover;
  border-radius: 8px;
  cursor: pointer;
  transition: transform 0.2s;
}

.journal-images img:hover {
  transform: scale(1.05);
}

.summary-tags {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.md-editor{
  border: none;
  border-radius: 0;
    overflow: hidden;
}

.tag {
  padding: 0.25rem 0.5rem;
  background: #f0f0f0;
  color: #666;
  font-size: 0.8rem;
  border-radius: 4px;
}

.empty-detail {
  background: #fff;
  border-radius: 8px;
  padding: 3rem;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.empty-detail p {
  color: #999;
  margin-bottom: 1rem;
}

.add-btn {
  padding: 0.5rem 1rem;
  background: #42b883;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: background 0.2s;
}

.add-btn:hover {
  background: #369970;
}

/* 图片预览弹窗 */
.image-preview-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.image-preview-content {
  position: relative;
  max-width: 90%;
  max-height: 90%;
}

.image-preview-content img {
  max-width: 100%;
  max-height: 90vh;
  object-fit: contain;
  border-radius: 8px;
}

.close-preview-btn {
  position: absolute;
  top: -40px;
  right: 0;
  background: none;
  border: none;
  color: #fff;
  font-size: 2rem;
  cursor: pointer;
  line-height: 1;
}

.close-preview-btn:hover {
  color: #ccc;
}
</style>
